import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { queryClient } from '../lib/react-query'
import { useAuth } from './AuthContext'
import { KNOWN_ORGS } from '../config/organizations'

interface OrganizationSettings {
  brandName: string
  primaryColor: string
  backgroundColor: string
  accentColor: string
  textColor: string
  darkMode: boolean
  facilityPrefix: string
  timeSlotDuration: number
  defaultFacilities: string[]
  defaultSessionTypes: string[]
  emailSettings: {
    sendgridApiKey: string
    emailFrom: string
    emailFromName: string
  }
  businessSettings: {
    address: string
    phone: string
    email: string
    website: string
    timezone: string
  }
  // Remote session settings
  allowClientRecording?: boolean // Whether clients can record remote sessions (coaches can always record)
  remoteSession?: {
    allowClientRecording?: boolean
  }
}

interface SubscriptionInfo {
  plan_code: 'trainer' | 'starter' | 'professional' | 'enterprise'
  tier_type: 'trainer' | 'facility'
  features: Record<string, boolean>
  status: 'trial' | 'active' | 'past_due' | 'cancelled' | 'suspended'
  limits: {
    max_facilities?: number
    max_members?: number
    max_clients?: number
    max_staff_accounts?: number
  }
}

interface Organization {
  id: string
  name: string
  subdomain: string | null
  settings: OrganizationSettings
  is_active: boolean
  created_at: string
  updated_at: string
  subscription?: SubscriptionInfo
  time_slot_duration?: number
}

interface OrganizationContextType {
  organization: Organization | null
  loading: boolean
  error: Error | null
  refreshOrganization: () => Promise<void>
  updateOrganizationSettings: (settings: Partial<OrganizationSettings>) => Promise<void>
  switchOrganization?: (orgId: string) => Promise<void>
}

// Default organization settings (Black & Silver)
const DEFAULT_ORGANIZATION: Organization = {
  id: KNOWN_ORGS.DEFAULT,
  name: 'Northstar Hockey Academy',
  subdomain: 'default',
  settings: {
    brandName: 'Facility Manager',
    primaryColor: '#9CA3AF',
    backgroundColor: '#000000',
    accentColor: '#9CA3AF',
    textColor: '#ffffff',
    darkMode: true,
    facilityPrefix: 'Rink',
    timeSlotDuration: 15,
    defaultFacilities: ['Rink 1', 'Rink 2'],
    defaultSessionTypes: ['Rental', 'Class'],
    emailSettings: {
      sendgridApiKey: '',
      emailFrom: 'noreply@aifacilitymanager.com',
      emailFromName: 'Facility Manager'
    },
    businessSettings: {
      address: '',
      phone: '',
      email: '',
      website: '',
      timezone: 'America/Toronto'
    },
    // Remote session defaults
    allowClientRecording: true // Allow clients to record by default
  },
  is_active: true,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user, isSuperAdmin } = useAuth()
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  
  // Check if we're in platform mode based on localStorage
  const getSuperAdminMode = () => {
    const mode = localStorage.getItem('super-admin-mode')
    return mode === 'platform'
  }
  
  const isInPlatformMode = isSuperAdmin && getSuperAdminMode()
  
  // Organization locking mechanism to prevent switching
  const [organizationLocked, setOrganizationLocked] = useState(false)
  const [lockedOrgId, setLockedOrgId] = useState<string | null>(null)
  
  // Persistence helpers
  const persistOrganization = (orgId: string) => {
    console.log('🔒 Persisting organization:', orgId)
    localStorage.setItem('locked-organization-id', orgId)
    localStorage.setItem('locked-organization-timestamp', new Date().toISOString())
  }
  
  const getPersistedOrganization = (): string | null => {
    const orgId = localStorage.getItem('locked-organization-id')
    const timestamp = localStorage.getItem('locked-organization-timestamp')
    
    if (orgId && timestamp) {
      // Check if lock is less than 24 hours old
      const lockAge = Date.now() - new Date(timestamp).getTime()
      if (lockAge < 24 * 60 * 60 * 1000) {
        console.log('🔒 Found persisted organization:', orgId)
        return orgId
      } else {
        console.log('🔒 Persisted organization expired')
        localStorage.removeItem('locked-organization-id')
        localStorage.removeItem('locked-organization-timestamp')
      }
    }
    return null
  }

  // Fetch organization data
  const fetchOrganization = async () => {
    // Don't fetch if there's no user
    if (!user?.id) {
      console.log('📋 No user present, skipping organization fetch')
      setLoading(false)
      setOrganization(null)
      return
    }
    
    // Skip organization fetch if super admin is in platform mode
    if (isInPlatformMode) {
      console.log('🌐 Super admin in platform mode, skipping organization fetch')
      setLoading(false)
      setOrganization(null)
      return
    }
    
    try {
      setLoading(true)
      setError(null)

      // CRITICAL: Use server-side verification for organization
      const { data: contextData, error: contextError } = await supabase
        .rpc('initialize_user_organization_context')

      if (contextError) {
        // Don't log network errors (common during testing/offline)
        if (contextError.message?.includes('Failed to fetch')) {
          console.log('Organization context fetch skipped (network unavailable)')
        } else {
          console.error('❌ Failed to initialize organization context:', contextError)
        }
        setError(new Error('Failed to verify organization access'))
        setOrganization(null)
        return
      }
      
      if (!contextData?.success || !contextData?.organization) {
        // Check if user is in activation process
        if (contextData?.is_activating) {
          console.log('🔄 User account is being activated, organization will be assigned soon')
          setError(null)
          setOrganization(null)
          // Set a timeout to retry in a few seconds
          setTimeout(() => {
            fetchOrganization()
          }, 3000)
          return
        }
        
        console.error('❌ No valid organization for user:', contextData?.error)
        setError(new Error(contextData?.error || 'No organization access'))
        setOrganization(null)
        return
      }
      
      const organizationData = contextData.organization
      console.log('✅ Organization verified server-side:', {
        id: organizationData.id,
        name: organizationData.name,
        verified_at: contextData.verified_at
      })
      
      // Fetch subscription information
      try {
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('organization_subscriptions')
          .select(`
            *,
            subscription_plans(*)
          `)
          .eq('organization_id', organizationData.id)
          .maybeSingle()

        if (subscriptionData && subscriptionData.subscription_plans) {
          const plan = subscriptionData.subscription_plans
          organizationData.subscription = {
            plan_code: plan.code,
            tier_type: plan.tier_type,
            features: plan.features || {},
            status: subscriptionData.status,
            limits: {
              max_facilities: plan.max_facilities,
              max_members: plan.max_members,
              max_clients: plan.max_clients,
              max_staff_accounts: plan.max_staff_accounts
            }
          }
        } else {
          // Use default subscription
          organizationData.subscription = {
            plan_code: 'professional',
            tier_type: 'facility',
            features: {
              facility_management: true,
              booking_system: true,
              member_management: true,
              advanced_reporting: true,
              payment_processing: true,
              bulk_communications: true,
              api_access: true,
              custom_branding: true,
              account_credits: true,
              priority_support: true
            },
            status: 'active',
            limits: {
              max_facilities: 5,
              max_members: 500,
              max_clients: null,
              max_staff_accounts: 15
            }
          }
        }
      } catch (subError) {
        console.warn('Subscription query failed:', subError)
      }
      
      // Apply any necessary data migrations
      if (organizationData.settings && !organizationData.settings.branding) {
        // Migrate old flat structure to nested
        if (organizationData.settings.primaryColor || organizationData.settings.backgroundColor) {
          organizationData.settings = {
            branding: {
              brandName: organizationData.settings.brandName || organizationData.name,
              primaryColor: organizationData.settings.primaryColor || '#9CA3AF',
              backgroundColor: organizationData.settings.backgroundColor || '#000000',
              accentColor: organizationData.settings.accentColor || '#9CA3AF',
              textColor: organizationData.settings.textColor || '#ffffff',
              darkMode: organizationData.settings.darkMode !== false
            },
            facilities: organizationData.settings.facilities || {
              facilityPrefix: organizationData.settings.facilityPrefix || 'Rink',
              defaultFacilities: organizationData.settings.defaultFacilities || ['Rink 1', 'Rink 2'],
              defaultSessionTypes: organizationData.settings.defaultSessionTypes || ['Rental', 'Class'],
              timeSlotDuration: organizationData.settings.timeSlotDuration || 60
            },
            email: organizationData.settings.email || {
              fromName: organizationData.name,
              replyTo: `info@${organizationData.subdomain || 'example'}.com`
            },
            business: organizationData.settings.business || {
              timezone: 'America/Toronto'
            }
          }
        }
      }
      
      console.log('✅ Organization context initialized:', {
        id: organizationData.id,
        name: organizationData.name,
        verified: true
      })
      
      setOrganization(organizationData)
      
      // CRITICAL: Lock and persist organization to prevent switching
      persistOrganization(organizationData.id)
      setOrganizationLocked(true)
      setLockedOrgId(organizationData.id)
      console.log('🔒 Organization locked and persisted:', organizationData.id)
      
      // Mark organization as loaded globally for post-import recovery
      if (typeof window !== 'undefined') {
        try {
          window.__organizationLoaded = true
          window.__organizationId = organizationData.id
          // Also save to localStorage as backup
          localStorage.setItem('last-known-org-id', organizationData.id)
        } catch (e) {
          console.warn('Could not set global organization markers:', e)
        }
      }
    } catch (err) {
      console.error('Error in fetchOrganization:', err)
      setError(err as Error)
      // DON'T use default on error - this causes the Hockey Factory fallback!
      // Clear any bad persistence
      localStorage.removeItem('locked-organization-id')
      localStorage.removeItem('locked-organization-timestamp')
    } finally {
      setLoading(false)
    }
  }

  // Refresh organization data
  const refreshOrganization = async () => {
    await fetchOrganization()
  }
  
  // Switch organization (for super admins or explicit switches)
  const switchOrganization = async (newOrgId: string) => {
    console.log('🔄 Switching organization to:', newOrgId)
    
    // Clear locks and persistence
    setOrganizationLocked(false)
    setLockedOrgId(null)
    localStorage.removeItem('locked-organization-id')
    localStorage.removeItem('locked-organization-timestamp')
    localStorage.removeItem('selectedOrganizationId')
    
    // If super admin, store the selection
    if (user?.profile?.is_super_admin) {
      localStorage.setItem('selectedOrganizationId', newOrgId)
    }
    
    // Clear all cached query data to prevent cross-org data leakage
    queryClient.clear()

    // Fetch new organization
    await fetchOrganization()
  }

  // Update organization settings
  const updateOrganizationSettings = async (newSettings: Partial<OrganizationSettings>) => {
    if (!organization) return

    try {
      const updatedSettings = {
        ...organization.settings,
        ...newSettings
      }

      const { error: updateError } = await supabase
        .from('organizations')
        .update({ 
          settings: updatedSettings,
          updated_at: new Date().toISOString()
        })
        .eq('id', organization.id)

      if (updateError) {
        throw updateError
      }

      // Update local state
      setOrganization({
        ...organization,
        settings: updatedSettings,
        updated_at: new Date().toISOString()
      })
    } catch (err) {
      console.error('Error updating organization settings:', err)
      throw err
    }
  }

  // Load organization on mount and when user ID changes (not entire user object)
  useEffect(() => {
    // Check platform mode inside the effect
    const checkPlatformMode = isSuperAdmin && localStorage.getItem('super-admin-mode') === 'platform'
    
    // Skip everything if super admin is in platform mode
    if (checkPlatformMode) {
      console.log('🌐 Platform mode active, skipping organization logic')
      setOrganization(null)
      setLoading(false)
      return
    }
    
    // Only fetch if we don't have a locked organization
    if (user?.id && !organizationLocked) {
      console.log('🔄 User ID detected, fetching organization')
      fetchOrganization()
    } else if (!user?.id) {
      // Clear locks on logout
      console.log('🔄 No user, clearing organization locks')
      setOrganizationLocked(false)
      setLockedOrgId(null)
      localStorage.removeItem('locked-organization-id')
      localStorage.removeItem('locked-organization-timestamp')
      
      // DON'T use default organization - let login page handle unauthenticated state
      setOrganization(null)
      setLoading(false)
    } else if (organizationLocked) {
      console.log('🔒 Organization locked, skipping fetch')
      setLoading(false)
    }
  }, [user?.id, isSuperAdmin]) // Watch user ID and super admin status
  
  // Listen for post-import refresh events
  useEffect(() => {
    const handlePostImportRefresh = async () => {
      console.log('📊 Post-import organization refresh triggered')
      
      // Clear any cached organization data
      if (typeof window !== 'undefined') {
        try {
          window.__organizationLoaded = false
          window.__organizationId = null
        } catch (e) {
          console.warn('Could not clear global organization markers:', e)
        }
      }
      
      if (user) {
        // Add a small delay to ensure auth is stable
        await new Promise(resolve => setTimeout(resolve, 500))
        await fetchOrganization()
      }
    }
    
    window.addEventListener('refresh-organization', handlePostImportRefresh)
    return () => window.removeEventListener('refresh-organization', handlePostImportRefresh)
  }, [user])

  // Subscribe to organization changes
  useEffect(() => {
    if (!organization) return

    const subscription = supabase
      .channel(`organization:${organization.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'organizations',
          filter: `id=eq.${organization.id}`
        },
        (payload) => {
          console.log('Organization updated:', payload)
          setOrganization(payload.new as Organization)
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [organization?.id])

  return (
    <OrganizationContext.Provider
      value={{
        organization,
        loading,
        error,
        refreshOrganization,
        updateOrganizationSettings,
        switchOrganization
      }}
    >
      {children}
    </OrganizationContext.Provider>
  )
}

// Helper hook to get organization settings with defaults
export function useOrganizationSettings() {
  const { organization } = useOrganization()
  
  if (!organization?.settings) {
    return DEFAULT_ORGANIZATION.settings
  }
  
  const settings = organization.settings
  
  // Handle nested structure from database
  if (settings.branding || settings.facilities) {
    const branding = settings.branding || {}
    const facilities = settings.facilities || {}
    const business = settings.business || {}
    const email = settings.email || {}
    
    // Return flattened structure for backward compatibility
    return {
      // Branding
      brandName: branding.brandName || settings.brandName || DEFAULT_ORGANIZATION.settings.brandName,
      primaryColor: branding.primaryColor || settings.primaryColor || DEFAULT_ORGANIZATION.settings.primaryColor,
      backgroundColor: branding.backgroundColor || settings.backgroundColor || DEFAULT_ORGANIZATION.settings.backgroundColor,
      accentColor: branding.accentColor || settings.accentColor || DEFAULT_ORGANIZATION.settings.accentColor,
      textColor: branding.textColor || settings.textColor || DEFAULT_ORGANIZATION.settings.textColor,
      darkMode: branding.darkMode !== undefined ? branding.darkMode : (settings.darkMode !== undefined ? settings.darkMode : DEFAULT_ORGANIZATION.settings.darkMode),
      
      // Facilities
      facilityPrefix: facilities.facilityPrefix || settings.facilityPrefix || DEFAULT_ORGANIZATION.settings.facilityPrefix,
      timeSlotDuration: facilities.timeSlotDuration || settings.timeSlotDuration || DEFAULT_ORGANIZATION.settings.timeSlotDuration,
      defaultFacilities: facilities.defaultFacilities || settings.defaultFacilities || DEFAULT_ORGANIZATION.settings.defaultFacilities,
      defaultSessionTypes: facilities.defaultSessionTypes || settings.defaultSessionTypes || DEFAULT_ORGANIZATION.settings.defaultSessionTypes,
      
      // Email
      emailSettings: {
        sendgridApiKey: email.sendgridApiKey || settings.emailSettings?.sendgridApiKey || '',
        emailFrom: email.replyTo || settings.emailSettings?.emailFrom || DEFAULT_ORGANIZATION.settings.emailSettings.emailFrom,
        emailFromName: email.fromName || settings.emailSettings?.emailFromName || DEFAULT_ORGANIZATION.settings.emailSettings.emailFromName
      },
      
      // Business
      businessSettings: {
        address: business.address || settings.businessSettings?.address || '',
        phone: business.phone || settings.businessSettings?.phone || '',
        email: business.email || settings.businessSettings?.email || '',
        website: business.website || settings.businessSettings?.website || '',
        timezone: business.timezone || settings.businessSettings?.timezone || DEFAULT_ORGANIZATION.settings.businessSettings.timezone
      },

      // Remote session settings
      allowClientRecording: settings.remoteSession?.allowClientRecording ?? settings.allowClientRecording ?? DEFAULT_ORGANIZATION.settings.allowClientRecording,

      // Keep the nested structure accessible
      branding,
      facilities,
      email,
      business
    }
  }
  
  // Return flat structure as-is
  return settings
}